import PropTypes from 'prop-types';
import React, { Component, cloneElement } from 'react';
import lodash from 'lodash';
import { Grid, GridPanel } from 'common/components/layout/GridPanel';
import DescriptorValue from 'common/components/DescriptorValue/DescriptorValue';
import styles from 'common/components/DataGrid/DataGrid.scss';

const dataGridFieldProperty = PropTypes.shape({
    /**
     * fieldKey: a unique key for rendering
     */
    fieldKey: PropTypes.string.isRequired,
    /**
     * valueSelector: either a function or a string used to interpret a value from a dataItem
     *  string: a lodash path (e.g. foo.bar) to a value relative to the dataItem
     *  function: has a signature of (dataItem, index, field, dataItems) => value
     *      use this to get a value from a dataItem in a custom way.
     */
    valueSelector: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.string,
    ]),
    /**
     * dataTypeSelector: either a function or a string used to interpret a dataType for the value
     *  string: a lodash path (e.g. foo.bar) to a value relative to the dataItem to serve as the dataType
     *  function: has a signature of (dataItem, index, field, dataItems) => string
     *      use this to get a dataType in a custom way.
     */
    dataTypeSelector: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.string,
    ]),
    /**
     * React element (not component) that serves as a template for content to be rendered.
     * If not provided, a simple DescriptorValue component is instead used to render the value.
     *
     * The cloned element is passed additional props: value, dataType, rawData, field,
     *  index, majorIndex, minorIndex, row, rowSpan, rowAlign, column, columnSpan, columnAlign
     */
    contentTemplate: PropTypes.element,
    /**
     * css class for the content of each data item
     */
    contentClassName: PropTypes.string,
    /**
     * horizontal alignment of grid items in this field. overrides the rowAlign property of the data grid.
     */
    contentRowAlign: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /**
     * vertical alignment of grid items in this field. overrides the columnAlign property of the data grid.
     */
    contentColumnAlign: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /**
     * If headerTemplate is not provided, then this value is used as the text for the header
     */
    header: PropTypes.string,
    /**
     * css class for the content of each header item
     */
    headerClassName: PropTypes.string,
    /**
     * React element (not component) that serves as a template for header content that is to be rendered
     * If not provided, a simple small_text DescriptorValue component is used instead
     *
     * The cloned element is passed additional props: text, field, index, majorIndex, minorIndex,
     * row, rowSpan, rowAlign, column, columnSpan, columnAlign,
     */
    headerTemplate: PropTypes.element,
    /**
     * horizontal alignment of header grid items
     */
    headerRowAlign: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /**
     * vertical alignment of header grid items
     */
    headerColumnAlign: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /**
     *  this is used to set a custom size for the cell or grid. In this context, size refers to the length along
     *  the minor direction. (The minor direction is the one parallel to the fields of a data item)
     *
     *  if the value is a string, it will be used verbatim. If the value is a number, it will be treated the same
     *  as the weight field.
     */
    length: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    /**
     * If length is not provided, then this number will define the relative proportion of size for each grid cell
     * along the minor direction.
     */
    weight: PropTypes.number,
    /**
     * A field with a lower number will appear before a field with higher number along the minor direction.
     * For a horizontal orientation, a field with a lower number will appear to the left of a field with a higher number.
     * For a vertical orientation, a field with a lower number will appear above a field with a higher number.
     */
    displayOrder: PropTypes.number,
    /**
     * default value that is displayed in a cell if the selected value is falsy.
     */
    defaultValue: PropTypes.any,
});

const dataGridProperties = PropTypes.shape({
    /**
     * The data source for the data grid. Can be an array of object, or a promise that returns an array of objects,
     * or a function that returns either of those. If it is a promise, then the grid will show nothing during a
     * loading state.
     */
    data: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.func,
        PropTypes.instanceOf(Promise),
    ]),
    /**
     * the default horizontal alignment for fields in the grid
     */
    rowAlign: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /**
     * the default vertical alignment for fields in the grid
     */
    columnAlign: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /**
     * the fields of the grid. at least one field should be supplied, and should conform to the shape
     * specified by the dataGridFieldProperty variable above.
     */
    fields: PropTypes.arrayOf(dataGridFieldProperty).isRequired,
    /**
     * Determines if the grid should be rendered horizontally or vertically.
     * If the orientation is horizontal, then each new data item will appear top to bottom, and each
     * field for a single data item will appear left to right.
     * If the orientation is vertical, then each new data item will appear left to right, and each field
     * for a single data item will appear top to bottom.
     */
    orientation: PropTypes.oneOf(['vertical', 'horizontal']).isRequired,
    /**
     * if true, then the headers will be shown. if false, the headers will not be shown.
     */
    showHeaders: PropTypes.bool.isRequired,
    /**
     * css class that is provided to the containing div element.
     */
    className: PropTypes.string,
});

const dataGridDefaultProps = {
    data: null,
    fields: null,
    orientation: 'vertical',
    showHeaders: true,
    rowAlign: 'start',
    columnAlign: 'center',
};

export class DataGrid extends Component {

    constructor(...args) {
        super(...args);
        this.state = {
            loading: true,
            data: null,
        };
    }

    async resolveData() {
        const { data } = this.props;
        return lodash.isFunction(data) ? data() : data;
    }

    async componentDidMount() {
        await this.onDataUpdate();
    }

    async componentDidUpdate() {
        if (!lodash.isEqual(this.state.data, this.props.data)) {
            await this.onDataUpdate();
        }
    }

    async onDataUpdate() {
        const sortedFields = sortFieldsByDisplayOrder(this.props.fields);
        const data = await this.resolveData();
        this.setState({
            data: data,
            loading: false,
            fields: sortedFields,
        });
    }

    get isLoading() {
        return this.state.loading;
    }

    get data() {
        return this.state.data;
    }

    get orientation() {
        return this.props.orientation;
    }

    get showHeaders() {
        return this.props.showHeaders;
    }

    get rowAlign() {
        return this.props.rowAlign;
    }

    get columnAlign() {
        return this.props.columnAlign;
    }

    get fields() {
        return this.state.fields;
    }

    get className() {
        return this.props.className;
    }

    render() {
        if (this.isLoading) {
            return null;
        }
        const gridLayout = this.calculateGridLayout();
        const headerLayout = this.calculateHeaderLayout();
        const dataLayout = this.calculateDataLayout();

        const headerItems = lodash.map(headerLayout, header => {
            return <DataGridHeader key={header.key} header={header}/>;
        });

        const dataItems = lodash.map(dataLayout, dataItem => {
            return <DataGridItem key={dataItem.key} dataItem={dataItem}/>;
        });

        return (
            <Grid {...gridLayout}>
                {headerItems}
                {dataItems}
            </Grid>
        );
    }

    calculateGridLayout() {
        const {
            data,
            fields,
            orientation,
            showHeaders,
            rowAlign,
            columnAlign,
            className,
        } = this;
        const headerOffset = showHeaders ? 1 : 0;
        const majorDirectionDefinition = lodash.repeat('auto ', lodash.size(data) + headerOffset);
        const minorDirectionDefinition = [];
        lodash.forEach(fields, field => {
            minorDirectionDefinition.push(calculateFieldLength(field));
        });
        const isVertical = orientation === 'vertical';
        return {
            display: 'grid',
            gridRows: isVertical ? majorDirectionDefinition : minorDirectionDefinition,
            gridColumns: isVertical ? minorDirectionDefinition : majorDirectionDefinition,
            rowItemAlignment: rowAlign,
            columnItemAlignment: columnAlign,
            className,
        };
    }

    calculateHeaderLayout() {
        const {
            fields,
            orientation,
            showHeaders,
        } = this;
        if (!showHeaders) {
            return null;
        }
        const fieldsLength = lodash.size(fields);
        const isVertical = orientation === 'vertical';
        return lodash.map(fields, (field, fieldIndex) => {
            const majorIndex = 0;
            const minorIndex = fieldIndex + 1;
            return {
                key: makeCellKey(field.fieldKey, majorIndex, minorIndex),
                field: field,
                index: majorIndex * fieldsLength + fieldIndex,
                majorIndex,
                minorIndex,
                gridRow: isVertical ? majorIndex : minorIndex,
                gridColumn: isVertical ? minorIndex : majorIndex,
            };
        });
    }

    calculateDataLayout() {
        const {
            data,
            orientation,
            fields,
            showHeaders,
        } = this;
        const fieldsLength = lodash.size(fields);
        const isVertical = orientation === 'vertical';
        const headerOffset = showHeaders ? 1 : 0;
        return lodash.flatMap(data, (dataItem, index) => {
            return lodash.map(fields, (field, fieldIndex) => {
                const majorIndex = (index + headerOffset) + 1;
                const minorIndex = fieldIndex + 1;
                return {
                    key: makeCellKey(field.fieldKey, majorIndex, minorIndex),
                    value: selectDataValue(dataItem, field, index, data),
                    dataType: selectDataTypeValue(dataItem, field, index, data),
                    rawData: dataItem,
                    field: field,
                    majorIndex,
                    minorIndex,
                    index: majorIndex * fieldsLength + fieldIndex,
                    gridRow: isVertical ? majorIndex : minorIndex,
                    gridColumn: isVertical ? minorIndex : majorIndex,
                };
            });
        });
    }
}

DataGrid.propTypes = dataGridProperties;
DataGrid.defaultProps = dataGridDefaultProps;

function calculateFieldLength(field) {
    if (lodash.isNil(field.length)) {
        return `${field.weight || 1}fr`;
    }
    if (lodash.isFinite(field.length)) {
        return `${field.length}fr`;
    }
    return field.length;
}

function makeCellKey(...keyValues) {
    return keyValues.join('-');
}

function sortFieldsByDisplayOrder(fields) {
    const affineDisplayOffset = 1;
    const sumOfDisplayOrders = lodash.reduce(fields,
        (result, field) => result + lodash.isFinite(field.displayOrder) ? field.displayOrder : 0,
        affineDisplayOffset,
    );
    return lodash.orderBy(fields, (field, index) => {
        if (lodash.isFinite(field.displayOrder)) {
            return field.displayOrder;
        }
        return sumOfDisplayOrders + index;
    });
}

function selectDataValue(dataItem, field, index, dataItems) {
    const { valueSelector } = field;
    if (lodash.isFunction(valueSelector)) {
        return valueSelector(dataItem, field, index, dataItems);
    }
    return lodash.get(
        dataItem,
        valueSelector,
        lodash.isUndefined(field.defaultValue) ? null : field.defaultValue,
    );
}

function selectDataTypeValue(dataItem, field, index, dataItems) {
    const { dataTypeSelector } = field;
    if (lodash.isFunction(dataTypeSelector)) {
        return dataTypeSelector(dataItem, field, index, dataItems);
    }
    return lodash.get(
        dataItem,
        dataTypeSelector,
        'small_text',
    );
}

class DataGridHeader extends Component {

    get field() {
        return this.props.header.field;
    }

    get text() {
        return this.field.header;
    }

    get index() {
        return this.props.header.index;
    }

    get majorIndex() {
        return this.props.header.majorIndex;
    }

    get minorIndex() {
        return this.props.header.minorIndex;
    }

    get headerClassName() {
        return this.field.headerClassName;
    }

    get headerTemplate() {
        return this.field.headerTemplate;
    }

    get row() {
        return this.props.header.gridRow;
    }

    get rowSpan() {
        return 1;
    }

    get rowAlign() {
        return this.field.headerRowAlign || this.props.rowAlign;
    }

    get column() {
        return this.props.header.gridColumn;
    }

    get columnSpan() {
        return 1;
    }

    get columnAlign() {
        return this.field.headerColumnAlign || this.props.columnAlign;
    }

    render() {
        const {
            headerTemplate,
            headerClassName,
            text,
            field,
            index,
            majorIndex,
            minorIndex,
            row,
            rowSpan,
            rowAlign,
            column,
            columnSpan,
            columnAlign,
        } = this;

        let element;
        if (headerTemplate) {
            element = cloneElement(headerTemplate, {
                text,
                field,
                index,
                majorIndex,
                minorIndex,
                row,
                rowSpan,
                rowAlign,
                column,
                columnSpan,
                columnAlign,
            });
        } else {
            element = <span>{text}</span>;
        }

        return (
            <GridPanel
                gridRow={row}
                gridRowSpan={rowSpan}
                gridColumn={column}
                gridColumnSpan={columnSpan}
                rowAlign={rowAlign}
                columnAlign={columnAlign}
                className={`${styles.dataGridHeader} ${headerClassName}`}>
                { element }
            </GridPanel>
        );
    }
}

class DataGridItem extends Component {
    get dataItem() {
        return this.props.dataItem;
    }

    get value() {
        return this.dataItem.value;
    }

    get index() {
        return this.dataItem.index;
    }

    get majorIndex() {
        return this.dataItem.majorIndex;
    }

    get minorIndex() {
        return this.dataItem.minorIndex;
    }

    get rawData() {
        return this.dataItem.rawData;
    }

    get field() {
        return this.dataItem.field;
    }

    get dataType() {
        return this.dataItem.dataType;
    }

    get contentTemplate() {
        return this.field.contentTemplate;
    }

    get contentClassName() {
        return this.field.contentClassName;
    }

    get row() {
        return this.dataItem.gridRow;
    }

    get rowSpan() {
        return 1;
    }

    get rowAlign() {
        return this.field.contentRowAlign || this.props.rowAlign;
    }

    get column() {
        return this.dataItem.gridColumn;
    }

    get columnSpan() {
        return 1;
    }

    get columnAlign() {
        return this.field.contentColumnAlign || this.props.columnAlign;
    }

    render() {
        const {
            contentTemplate,
            contentClassName,
            value,
            dataType,
            field,
            index,
            majorIndex,
            minorIndex,
            rawData,
            row,
            rowSpan,
            rowAlign,
            column,
            columnSpan,
            columnAlign,
        } = this;

        let element;
        if (contentTemplate) {
            element = cloneElement(contentTemplate, {
                value,
                dataType,
                rawData,
                field,
                index,
                majorIndex,
                minorIndex,
                row,
                rowSpan,
                rowAlign,
                column,
                columnSpan,
                columnAlign,
            });
        } else {
            element = <DescriptorValue dataType={dataType} value={value}/>;
        }

        return (
            <GridPanel
                gridRow={row}
                gridRowSpan={rowSpan}
                gridColumn={column}
                gridColumnSpan={columnSpan}
                rowAlign={rowAlign}
                columnAlign={columnAlign}
                className={`${styles.dataGridItem} ${contentClassName || ''}`}>
                { element }
            </GridPanel>
        );
    }

}

